import React, { useState, useEffect } from 'react';
import { tripPlanModel, validateTripPlan } from '../../data/tripPlanModel';

const TripPlanForm = ({ 
  tripPlan = null, 
  onSubmit, 
  onCancel, 
  isLoading = false 
}) => {
  const [formData, setFormData] = useState({
    tripName: '',
    tripDate: '',
    tripLocation: '',
    tripTheme: '',
    participantCount: 1,
    description: '',
    requiredGirls: 1,
    budget: 0,
    duration: 1,
    specialRequirements: '',
    isPublic: false,
    status: 'draft',
    // Check-in configuration
    checkinOrganizers: false,
    checkinParticipants: false,
    checkinGirls: false,
    allowManualCheckin: true,
    allowQRCheckin: true
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Initialize form with existing trip plan data
  useEffect(() => {
    if (tripPlan) {
      setFormData({
        tripName: tripPlan.tripName || '',
        tripDate: tripPlan.tripDate || '',
        tripLocation: tripPlan.tripLocation || '',
        tripTheme: tripPlan.tripTheme || '',
        participantCount: tripPlan.participantCount || 1,
        description: tripPlan.description || '',
        requiredGirls: tripPlan.requiredGirls || 1,
        budget: tripPlan.budget || 0,
        duration: tripPlan.duration || 1,
        specialRequirements: tripPlan.specialRequirements || '',
        isPublic: tripPlan.isPublic || false,
        status: tripPlan.status || 'draft',
        // Check-in configuration
        checkinOrganizers: tripPlan.checkinConfig?.enabled?.organizers || false,
        checkinParticipants: tripPlan.checkinConfig?.enabled?.participants || false,
        checkinGirls: tripPlan.checkinConfig?.enabled?.girls || false,
        allowManualCheckin: tripPlan.checkinConfig?.settings?.allowManualCheckin !== false,
        allowQRCheckin: tripPlan.checkinConfig?.settings?.allowQRCheckin !== false
      });
    }
  }, [tripPlan]);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    const newValue = type === 'checkbox' ? checked : 
                   type === 'number' ? parseInt(value) || 0 : value;
    
    setFormData(prev => ({
      ...prev,
      [name]: newValue
    }));

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    console.log('🚀 Form submit started');
    console.log('📝 Form data:', formData);
    
    setIsSubmitting(true);

    try {
      // Validate form data
      console.log('🔍 Validating form data...');
      const validation = validateTripPlan(formData);
      console.log('✅ Validation result:', validation);
      
      if (!validation.isValid) {
        console.log('❌ Validation failed:', validation.errors);
        setErrors(validation.errors);
        setIsSubmitting(false);
        return;
      }

      // Clear errors
      setErrors({});

      // Submit form
      console.log('📤 Submitting form...');
      await onSubmit(formData);
      console.log('✅ Form submitted successfully');
    } catch (error) {
      console.error('❌ Error submitting trip plan:', error);
      setErrors({ submit: 'เกิดข้อผิดพลาดในการบันทึกข้อมูล' });
    } finally {
      setIsSubmitting(false);
    }
  };

  const getMinDate = () => {
    const today = new Date();
    today.setDate(today.getDate() + 1); // Minimum 1 day from today
    return today.toISOString().split('T')[0];
  };

  return (
    <div className="max-w-4xl mx-auto p-6">
      <div className="bg-white rounded-lg shadow-lg">
        <div className="p-6 border-b border-gray-200">
          <h2 className="text-2xl font-bold text-gray-900">
            {tripPlan ? 'แก้ไข Trip Plan' : 'สร้าง Trip Plan ใหม่'}
          </h2>
          <p className="text-gray-600 mt-2">
            {tripPlan ? 'แก้ไขข้อมูลทริปของคุณ' : 'วางแผนทริปของคุณเพื่อการจองคิวที่สะดวก'}
          </p>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Basic Information */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-gray-900">ข้อมูลพื้นฐาน</h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Trip Name */}
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  ชื่อทริป *
                </label>
                <input
                  type="text"
                  name="tripName"
                  value={formData.tripName}
                  onChange={handleInputChange}
                  className={`w-full p-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                    errors.tripName ? 'border-red-500' : 'border-gray-300'
                  }`}
                  placeholder="เช่น ทริปวันเกิด, ปาร์ตี้เพื่อน, งานธุรกิจ"
                />
                {errors.tripName && (
                  <p className="text-red-500 text-sm mt-1">{errors.tripName}</p>
                )}
              </div>

              {/* Trip Date */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  วันที่ทริป *
                </label>
                <input
                  type="date"
                  name="tripDate"
                  value={formData.tripDate}
                  onChange={handleInputChange}
                  min={getMinDate()}
                  className={`w-full p-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                    errors.tripDate ? 'border-red-500' : 'border-gray-300'
                  }`}
                />
                {errors.tripDate && (
                  <p className="text-red-500 text-sm mt-1">{errors.tripDate}</p>
                )}
              </div>

              {/* Trip Theme */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Theme ของทริป *
                </label>
                <select
                  name="tripTheme"
                  value={formData.tripTheme}
                  onChange={handleInputChange}
                  className={`w-full p-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                    errors.tripTheme ? 'border-red-500' : 'border-gray-300'
                  }`}
                >
                  <option value="">เลือก Theme</option>
                  {tripPlanModel.themeOptions.map(theme => (
                    <option key={theme.value} value={theme.value}>
                      {theme.icon} {theme.label}
                    </option>
                  ))}
                </select>
                {errors.tripTheme && (
                  <p className="text-red-500 text-sm mt-1">{errors.tripTheme}</p>
                )}
              </div>

              {/* Trip Location */}
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  สถานที่ที่พัก
                </label>
                <input
                  type="text"
                  name="tripLocation"
                  value={formData.tripLocation}
                  onChange={handleInputChange}
                  className={`w-full p-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                    errors.tripLocation ? 'border-red-500' : 'border-gray-300'
                  }`}
                  placeholder="เช่น โรงแรม ABC, บ้านพัก XYZ, สถานที่จัดงาน"
                />
                {errors.tripLocation && (
                  <p className="text-red-500 text-sm mt-1">{errors.tripLocation}</p>
                )}
              </div>
            </div>
          </div>

          {/* Trip Details */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-gray-900">รายละเอียดทริป</h3>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {/* Participant Count */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  จำนวนผู้เข้าร่วม *
                </label>
                <input
                  type="number"
                  name="participantCount"
                  value={formData.participantCount}
                  onChange={handleInputChange}
                  min="1"
                  max="100"
                  className={`w-full p-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                    errors.participantCount ? 'border-red-500' : 'border-gray-300'
                  }`}
                />
                {errors.participantCount && (
                  <p className="text-red-500 text-sm mt-1">{errors.participantCount}</p>
                )}
              </div>

              {/* Required Girls */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  จำนวนน้องที่ต้องการ *
                </label>
                <input
                  type="number"
                  name="requiredGirls"
                  value={formData.requiredGirls}
                  onChange={handleInputChange}
                  min="1"
                  max="50"
                  className={`w-full p-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                    errors.requiredGirls ? 'border-red-500' : 'border-gray-300'
                  }`}
                />
                {errors.requiredGirls && (
                  <p className="text-red-500 text-sm mt-1">{errors.requiredGirls}</p>
                )}
              </div>

              {/* Duration */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  ระยะเวลา (วัน)
                </label>
                <input
                  type="number"
                  name="duration"
                  value={formData.duration}
                  onChange={handleInputChange}
                  min="1"
                  max="30"
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            </div>

            {/* Description */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                รายละเอียดเบื้องต้น
              </label>
              <textarea
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                rows="4"
                maxLength="1000"
                className={`w-full p-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                  errors.description ? 'border-red-500' : 'border-gray-300'
                }`}
                placeholder="อธิบายรายละเอียดของทริป เช่น กิจกรรมที่ต้องการ, เวลา, ความต้องการพิเศษ"
              />
              <div className="flex justify-between items-center mt-1">
                {errors.description && (
                  <p className="text-red-500 text-sm">{errors.description}</p>
                )}
                <p className="text-gray-500 text-sm ml-auto">
                  {formData.description.length}/1000 ตัวอักษร
                </p>
              </div>
            </div>

            {/* Special Requirements */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                ความต้องการพิเศษ
              </label>
              <textarea
                name="specialRequirements"
                value={formData.specialRequirements}
                onChange={handleInputChange}
                rows="3"
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="เช่น ต้องการน้องที่พูดภาษาอังกฤษได้, ต้องการน้องที่เล่นกีตาร์ได้"
              />
            </div>
          </div>

          {/* Additional Settings */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-gray-900">การตั้งค่าเพิ่มเติม</h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Budget */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  งบประมาณ (บาท)
                </label>
                <input
                  type="number"
                  name="budget"
                  value={formData.budget}
                  onChange={handleInputChange}
                  min="0"
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="0"
                />
              </div>

              {/* Status */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  สถานะ
                </label>
                <select
                  name="status"
                  value={formData.status}
                  onChange={handleInputChange}
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  {tripPlanModel.statusOptions.map(status => (
                    <option key={status.value} value={status.value}>
                      {status.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Public Setting */}
            <div className="flex items-center">
              <input
                type="checkbox"
                name="isPublic"
                checked={formData.isPublic}
                onChange={handleInputChange}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              />
              <label className="ml-2 block text-sm text-gray-900">
                ให้คนอื่นสามารถเห็นทริปนี้ได้ (สาธารณะ)
              </label>
            </div>
          </div>

          {/* Check-in Configuration */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-gray-900">การตั้งค่า Check-in</h3>
            <p className="text-sm text-gray-600">
              ตั้งค่าการ check-in สำหรับกลุ่มต่างๆ ในทริป (สามารถแก้ไขได้ภายหลัง)
            </p>
            
            <div className="bg-gray-50 rounded-lg p-4 space-y-4">
              {/* Enable Check-in for Groups */}
              <div>
                <h4 className="text-md font-medium text-gray-900 mb-3">เปิดใช้งาน Check-in สำหรับกลุ่ม</h4>
                <div className="space-y-3">
                  {/* Organizers */}
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <span className="text-xl">👨‍💼</span>
                      <div>
                        <div className="font-medium text-gray-900">ผู้จัด/ผู้ช่วยจัด</div>
                        <div className="text-sm text-gray-600">ผู้จัดทริปและผู้ช่วยจัด</div>
                      </div>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        name="checkinOrganizers"
                        checked={formData.checkinOrganizers || false}
                        onChange={handleInputChange}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                    </label>
                  </div>

                  {/* Participants */}
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <span className="text-xl">👥</span>
                      <div>
                        <div className="font-medium text-gray-900">ผู้เข้าร่วมกิจกรรม</div>
                        <div className="text-sm text-gray-600">ผู้เข้าร่วมกิจกรรมทั้งหมด</div>
                      </div>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        name="checkinParticipants"
                        checked={formData.checkinParticipants || false}
                        onChange={handleInputChange}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                    </label>
                  </div>

                  {/* Girls */}
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <span className="text-xl">👧</span>
                      <div>
                        <div className="font-medium text-gray-900">น้องๆ</div>
                        <div className="text-sm text-gray-600">น้องๆ ที่เข้าร่วมทริป</div>
                      </div>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        name="checkinGirls"
                        checked={formData.checkinGirls || false}
                        onChange={handleInputChange}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                    </label>
                  </div>
                </div>
              </div>

              {/* Check-in Methods */}
              <div>
                <h4 className="text-md font-medium text-gray-900 mb-3">วิธีการ Check-in</h4>
                <div className="space-y-3">
                  {/* Manual Check-in */}
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <span className="text-xl">👆</span>
                      <div>
                        <div className="font-medium text-gray-900">Manual Check-in</div>
                        <div className="text-sm text-gray-600">อนุญาตให้ผู้จัดกดยืนยัน check-in เอง</div>
                      </div>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        name="allowManualCheckin"
                        checked={formData.allowManualCheckin !== false}
                        onChange={handleInputChange}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                    </label>
                  </div>

                  {/* QR Code Check-in - Temporarily Hidden */}
                  {/* <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <span className="text-xl">📱</span>
                      <div>
                        <div className="font-medium text-gray-900">QR Code Check-in</div>
                        <div className="text-sm text-gray-600">สร้าง QR code ให้น้องสแกนเพื่อ check-in</div>
                      </div>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        name="allowQRCheckin"
                        checked={formData.allowQRCheckin !== false}
                        onChange={handleInputChange}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                    </label>
                  </div> */}
                </div>
              </div>
            </div>
          </div>

          {/* Submit Error */}
          {errors.submit && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <p className="text-red-800 text-sm">{errors.submit}</p>
            </div>
          )}

          {/* Actions */}
          <div className="flex justify-end gap-4 pt-6 border-t border-gray-200">
            <button
              type="button"
              onClick={onCancel}
              className="px-6 py-3 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition"
            >
              ยกเลิก
            </button>
            <button
              type="submit"
              disabled={isSubmitting || isLoading}
              className="px-6 py-3 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSubmitting || isLoading ? 'กำลังบันทึก...' : (tripPlan ? 'อัพเดท' : 'สร้างทริป')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default TripPlanForm;


